// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { SessionUser } from '../types';
import { apiRequest } from '../lib/api';

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  can: (permissionKey: string) => boolean;
  login: (email: string, password: string) => Promise<{ forcePasswordChange: boolean }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<SessionUser | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async (): Promise<SessionUser | null> => {
    try {
      const userData = await apiRequest<SessionUser>('/api/auth/me');
      setUser(userData);
      return userData;
    } catch (err: any) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const can = useCallback((permissionKey: string): boolean => {
    if (!user) return false;
    if (user.role?.isSystemRole) return true;
    return user.permissions?.includes(permissionKey) || false;
  }, [user]);

  const login = async (email: string, password: string): Promise<{ forcePasswordChange: boolean }> => {
    const res = await apiRequest<{ message: string; forcePasswordChange: boolean }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    const refreshedUser = await refreshUser();
    return {
      forcePasswordChange: res.forcePasswordChange || Boolean(refreshedUser?.forcePasswordChange),
    };
  };

  const logout = async () => {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } catch {
      // Ignore network errors on logout
    } finally {
      setUser(null);
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        can,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
